/**
 * Returns mini-section -- "Returns by Horizon" dashboard teaser: a 2x2 grid
 * of grouped-bar charts, one per asset class (grouped bars, opacity
 * gradient 1.0/0.5/0.25, hover templates, y-axis sync).
 */
import { useMemo } from 'react';
import DashboardCard from '@/features/dashboard/components/DashboardCard';
import QueryState from '@/components/QueryState';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import { useMarsNominalReal } from '@/features/dashboard/api';
import { ASSET_COLORS } from '@/lib/plotly/colors';
import { chartLayout } from '@/lib/plotly/layoutPresets';
import {
  buildNominalRealTraces,
  computeNominalRealYRange,
  groupNominalReal,
  type HorizonEntry,
} from '@/lib/plotly/nominalRealChart';
import type { Data, Layout } from 'plotly.js';

/**
 * Asset class config. The API display strings map to canonical
 * `ASSET_COLORS` keys: "Fixed Income" → Bonds, "Inflation Swaps" → Inflation.
 */
const ASSET_CLASSES: ReadonlyArray<{
  key: string;
  label: string;
  color: string;
}> = [
  { key: 'Equities', label: 'Equities', color: ASSET_COLORS.Equities },
  { key: 'Fixed Income', label: 'Fixed Income', color: ASSET_COLORS.Bonds },
  { key: 'Commodities', label: 'Commodities', color: ASSET_COLORS.Commodities },
  { key: 'Inflation Swaps', label: 'Inflation Swaps', color: ASSET_COLORS.Inflation },
];

/**
 * Build the cell layout for one asset.
 *
 * Composes the bar family preset with the repo-wide chart convention
 * (`chartLayout` -> centered title, horizontal legend BETWEEN title
 * and chart drawing area, inner ticks). Mini-section font sizes (10pt)
 * keep the dashboard cells readable at 320px height. Y-range / dashed
 * zero-line / `barmode: 'group'` are applied last as cell-specific overrides.
 */
function buildCellLayout(
  label: string,
  yRange: [number, number] | null,
  hasNegative: boolean,
): Partial<Layout> {
  const shapes: Layout['shapes'] = hasNegative
    ? [
        {
          type: 'line',
          x0: 0,
          x1: 1,
          xref: 'paper',
          y0: 0,
          y1: 0,
          line: { color: 'black', width: 2, dash: 'dash' },
          layer: 'below',
        },
      ]
    : [];

  // Compose: bar family preset -> repo-wide convention with the cell title.
  // Tighter margins than the desktop default (l:50, r:20, t:60, b:40) so a
  // 320px-tall dashboard cell still has room for the title + legend stack.
  // `font: { size: 10 }` shrinks the legend / axis tick fonts to mini-context
  // sizes; the title font is set to 13 explicitly below since
  // `chartLayout` defaults to 15.
  const preset = chartLayout({
    family: 'bar',
    title: `${label} Expected Returns (%, Ann.)`,
    marginOverride: { l: 50, r: 20, t: 60, b: 40 },
  });

  const yaxis: Partial<Layout['yaxis']> = {
    ...preset.yaxis,
    title: { text: '% Ann.', standoff: 8 },
    tickformat: '.1f',
    showgrid: true,
    gridcolor: '#E0E0E0',
    gridwidth: 1,
  };

  if (yRange) {
    yaxis.range = [...yRange];
    yaxis.autorange = false;
  }

  return {
    ...preset,
    barmode: 'group',
    hovermode: 'x unified',
    // Override title font to 13 (preset default is 15) so it fits the mini cell.
    title: {
      ...(preset.title as object),
      font: { size: 13, family: 'Gill Sans MT', color: '#000000' },
    },
    // Override legend font size to 10 for the mini cell.
    legend: {
      ...(preset.legend as object),
      font: { size: 10, family: 'Gill Sans MT', color: '#000000' },
    },
    // Default font size for axis ticks in mini context.
    font: { size: 10, family: 'Gill Sans MT', color: '#000000' },
    xaxis: {
      ...preset.xaxis,
      title: { text: 'Investment Horizon (Years)', standoff: 8 },
      showgrid: false,
    },
    yaxis,
    shapes,
  };
}

interface AssetCellProps {
  label: string;
  color: string;
  entries: HorizonEntry[];
  yRange: [number, number] | null;
}

function AssetCell({ label, color, entries, yRange }: AssetCellProps): JSX.Element {
  const isCash = label.toLowerCase().includes('cash');
  const hasNegative = entries.some((e) => e.nominal < 0 || e.real < 0 || e.excess < 0);

  // Mini-section convention: 14pt value labels, `.1f` hover format; the
  // Excess trace is suppressed for cash cells.
  const data: Data[] = useMemo(
    () =>
      buildNominalRealTraces(entries, color, {
        textfontSize: 14,
        hoverFormat: '.1f',
        includeExcess: !isCash,
      }),
    [entries, color, isCash],
  );

  const layout = useMemo(
    () => buildCellLayout(label, yRange, hasNegative),
    [label, yRange, hasNegative],
  );

  return (
    <div
      className="overflow-hidden flex flex-col"
      style={{
        height: 320,
        borderRadius: 6,
        background: '#ffffff',
        padding: '0.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
      }}
    >
      <div className="flex-1 min-h-0">
        {entries.length > 0 ? (
          <PlotlyChart
            data={data}
            layout={layout}
            config={{ displayModeBar: false }}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-[color:var(--text-dark)] opacity-60">
            No data
          </div>
        )}
      </div>
    </div>
  );
}

/** 2x2 grid of asset cells. */
function AssetCellGrid({
  grouped,
  yRange,
}: {
  grouped: Map<string, HorizonEntry[]>;
  yRange: [number, number] | null;
}): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-4 max-w-[1100px] mx-auto">
      {ASSET_CLASSES.map((a) => (
        <AssetCell
          key={a.key}
          label={a.label}
          color={a.color}
          entries={grouped.get(a.key) ?? []}
          yRange={yRange}
        />
      ))}
    </div>
  );
}

export default function ReturnsMiniSection(): JSX.Element {
  const query = useMarsNominalReal('3y');

  const grouped = useMemo(() => groupNominalReal(query.data?.data), [query.data]);
  const yRange = useMemo(() => computeNominalRealYRange(grouped), [grouped]);

  return (
    <DashboardCard title="Returns by Horizon" href="/forecasts/returns" hrefLabel="Know More">
      {/* The 2x2 grid renders for any non-error state — per-cell "No data"
          covers an empty payload — so QueryState only gates loading/error. */}
      <QueryState
        query={query}
        isEmpty={() => false}
        loadingLabel="Loading"
        errorLabel="Failed to load asset returns."
      >
        {() => <AssetCellGrid grouped={grouped} yRange={yRange} />}
      </QueryState>
    </DashboardCard>
  );
}
